import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { PREMIUM_PAGE } from '@/constants/testIds';
import { Check, Sparkles, Lock, MapPin, ArrowLeft, Users, Wand2, FileText, Scale, Heart, Map, BarChart3, Gauge } from 'lucide-react';

const FREE = [
    { icon: Users, text: 'Up to 4 participants per trip' },
    { icon: Sparkles, text: '2 AI itinerary generations per trip' },
    { icon: Heart, text: 'Idea board and voting' },
    { icon: Scale, text: 'Preference forms and basic group profile' },
    { icon: Map, text: 'Travel personality profiles and shareable cards' },
    { icon: Gauge, text: 'Budget alignment and shared cost estimator' },
];

const PREMIUM = [
    { icon: Users, text: 'Unlimited participants' },
    { icon: Sparkles, text: 'Unlimited AI itinerary generations' },
    { icon: Wand2, text: 'AI conflict resolution suggestions' },
    { icon: BarChart3, text: 'Per-person satisfaction scores' },
    { icon: Scale, text: 'Personality balance indicators in itinerary' },
    { icon: Sparkles, text: '“Regenerate with emphasis” option' },
    { icon: Gauge, text: 'AI cost breakdown by destination' },
    { icon: FileText, text: 'PDF itinerary export' },
];

export default function Premium() {
    const { user } = useAuth();
    const ctaHref = user ? '/dashboard' : '/signup';

    return (
        <div className="min-h-screen flex flex-col" style={{ background: 'var(--ns-bg)' }}>
            {user ? (
                <Header />
            ) : (
                <header className="border-b border-[var(--ns-border)] bg-[var(--ns-bg)]">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                        <Link to="/" className="flex items-center gap-2">
                            <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--ns-navy)' }}>
                                <MapPin size={14} className="text-white" />
                            </span>
                            <span className="font-display text-lg font-semibold tracking-tight">NextStop</span>
                        </Link>
                        <div className="flex items-center gap-3">
                            <Link to="/login" className="text-sm text-[var(--ns-text2)] hover:text-[var(--ns-text)]">Sign in</Link>
                            <Link to="/signup">
                                <Button className="rounded-full h-9 px-4 text-white" style={{ background: 'var(--ns-navy)' }}>Create account</Button>
                            </Link>
                        </div>
                    </div>
                </header>
            )}

            <main data-testid={PREMIUM_PAGE.container} className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 w-full">
                <Link
                    to={ctaHref}
                    data-testid={PREMIUM_PAGE.backHomeButton}
                    className="inline-flex items-center gap-2 text-sm text-[var(--ns-text2)] hover:text-[var(--ns-text)] mb-6"
                >
                    <ArrowLeft size={14} /> Back to {user ? 'dashboard' : 'home'}
                </Link>

                {/* Hero */}
                <section className="text-center mb-10 sm:mb-14">
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-[var(--ns-soft)] text-[var(--ns-clay)] font-medium mb-4">
                        <Sparkles size={11} /> Premium
                    </span>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[var(--ns-text)] leading-[1.05]">
                        Free is for planning.<br />
                        <span style={{ color: 'var(--ns-clay)' }}>Premium</span> is for shipping the trip.
                    </h1>
                    <p className="text-base sm:text-lg text-[var(--ns-text2)] mt-5 max-w-2xl mx-auto leading-relaxed">
                        Compare what&apos;s in Free vs Premium. Premium is a one-time <strong>$6.99 SGD per trip</strong> — no subscriptions, no surprises.
                    </p>
                </section>

                {/* Two-column comparison */}
                <section className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                    {/* Free column */}
                    <article
                        data-testid={PREMIUM_PAGE.freeColumn}
                        className="ns-card p-7 sm:p-8 flex flex-col"
                    >
                        <header className="mb-5 pb-5 border-b border-[var(--ns-border)]">
                            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mb-2">Free</div>
                            <h2 className="font-display text-3xl font-semibold tracking-tight">$0</h2>
                            <p className="text-sm text-[var(--ns-text2)] mt-1">Plan together. Always free for the basics.</p>
                        </header>
                        <ul className="space-y-3 flex-1">
                            {FREE.map(({ icon: I, text }) => (
                                <li
                                    key={text}
                                    data-testid={PREMIUM_PAGE.freeFeature}
                                    className="flex items-start gap-3 text-sm text-[var(--ns-text)] leading-relaxed"
                                >
                                    <I size={16} className="text-[var(--ns-text3)] mt-0.5 flex-shrink-0" />
                                    <span>{text}</span>
                                </li>
                            ))}
                        </ul>
                        <Link to={user ? '/dashboard' : '/signup'} className="mt-6">
                            <Button
                                variant="outline"
                                className="w-full rounded-full h-11 border-[var(--ns-border)]"
                            >
                                {user ? 'Go to dashboard' : 'Start free'}
                            </Button>
                        </Link>
                    </article>

                    {/* Premium column */}
                    <article
                        data-testid={PREMIUM_PAGE.premiumColumn}
                        className="ns-card p-7 sm:p-8 flex flex-col relative overflow-hidden"
                        style={{ borderColor: 'var(--ns-clay)', borderWidth: 2 }}
                    >
                        <span
                            className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full font-medium text-white"
                            style={{ background: 'var(--ns-clay)' }}
                        >
                            <Sparkles size={11} /> Most popular
                        </span>
                        <header className="mb-5 pb-5 border-b border-[var(--ns-border)]">
                            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-clay)] mb-2 font-medium">Premium</div>
                            <h2 className="font-display text-3xl font-semibold tracking-tight">
                                $6.99 <span className="text-base font-normal text-[var(--ns-text2)]">SGD per trip</span>
                            </h2>
                            <p className="text-sm text-[var(--ns-text2)] mt-1">
                                One-time unlock. Everything in Free, plus the AI muscle that gets you out the door.
                            </p>
                        </header>
                        <ul className="space-y-3 flex-1">
                            {PREMIUM.map(({ icon: I, text }) => (
                                <li
                                    key={text}
                                    data-testid={PREMIUM_PAGE.premiumFeature}
                                    className="flex items-start gap-3 text-sm text-[var(--ns-text)] leading-relaxed"
                                >
                                    <I size={16} className="text-[var(--ns-clay)] mt-0.5 flex-shrink-0" />
                                    <span>{text}</span>
                                </li>
                            ))}
                        </ul>
                        <Link to={user ? '/dashboard' : '/signup'} className="mt-6">
                            <Button
                                className="w-full rounded-full h-11 text-white"
                                style={{ background: 'var(--ns-navy)' }}
                            >
                                <Lock size={14} className="mr-2" />
                                {user ? 'Choose a trip to unlock' : 'Sign up — then unlock'}
                            </Button>
                        </Link>
                    </article>
                </section>

                {/* Why premium */}
                <section className="mt-12 ns-card p-7 sm:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="flex items-start gap-3">
                        <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--ns-soft)' }}>
                            <Check size={16} className="text-[var(--ns-clay)]" />
                        </span>
                        <div>
                            <div className="font-display font-semibold tracking-tight">One-time. Per trip.</div>
                            <p className="text-sm text-[var(--ns-text2)] mt-1 leading-relaxed">No subscriptions. Pay once for the trip you&apos;re actually planning.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--ns-soft)' }}>
                            <Wand2 size={16} className="text-[var(--ns-clay)]" />
                        </span>
                        <div>
                            <div className="font-display font-semibold tracking-tight">Where the AI earns its keep.</div>
                            <p className="text-sm text-[var(--ns-text2)] mt-1 leading-relaxed">Conflict resolution, per-person satisfaction, and emphasis-aware regeneration only kick in here.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--ns-soft)' }}>
                            <FileText size={16} className="text-[var(--ns-clay)]" />
                        </span>
                        <div>
                            <div className="font-display font-semibold tracking-tight">A printable plan.</div>
                            <p className="text-sm text-[var(--ns-text2)] mt-1 leading-relaxed">Export the final itinerary as a clean PDF — perfect for the group chat or offline use.</p>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
